import { liveQuery } from 'dexie'
import { DEFAULT_FRAME, page } from 'inertiax-svelte'
import { derived, writable } from 'svelte/store'

import db from '~/lib/db'

/**
 * A store that never hands a subscriber the same value twice in a row.
 *
 * `writable` refuses same-value writes - its `set` bails on `safe_not_equal` -
 * but `subscribe` hands every new subscriber the current value just for showing
 * up, and a store that works its value out rather than being handed one can
 * deliver the same value twice: once from a `set` that raced the subscription,
 * once from the replay.
 *
 * A template's `$store` does not care, but a subscriber that tears work down
 * and sets it up again does: `session-sync.svelte.js` writes to localStorage on
 * every value it is handed, and the private Dexie stream in `setup.svelte.js`
 * hangs off this same store. So "the value did not actually change" must never
 * reach a subscriber.
 */
function distinct(source) {
  return {
    subscribe(run) {
      let last
      let seen = false

      return source.subscribe((value) => {
        if (seen && Object.is(last, value)) return
        seen = true
        last = value
        run(value)
      })
    }
  }
}

/**
 * The signed-in user's id, `undefined` while signed out. Everyone who has to
 * follow the user is built on this one store: every Dexie query below, the
 * cross-tab mirror in `session-sync.svelte.js` and the private stream in
 * `setup.svelte.js`.
 *
 * It moves when a request is answered, and never on a trip through the history.
 * Inertia keeps every page it has been to in the history entry, props and all,
 * so going back hands the app the props of a page as they were served - a page
 * from before signing in has no `current_user_id` at all (the controller
 * compacts it away), and the app would show itself signed out with the session
 * cookie still good (which is what the back button did after signing in, and
 * the other way round after signing out). `success` is the news that a response
 * has been swapped in; a restore from the history fires `navigate` instead, and
 * leaves this alone.
 */
const reported = writable(undefined)

// What the page this document was served with says, read on the first
// subscribe rather than up here: on the client this module is evaluated with
// the rest of the bundle, before Inertia has been handed the page out of the
// DOM - its props are still empty then, and a snapshot taken at that point
// would have a signed-in visitor looking signed out until their next request.
//
// That one cannot come off the event below with it: the first value this store
// hands out has to be the id the page was served with - `session-sync` leans on
// exactly that, comparing it against what other tabs announced - and the
// `navigate` that announces the served page is fired from a promise as Inertia
// boots, which may or may not beat the first subscriber to it.
let seeded = false

const served = {
  subscribe(run) {
    if (!seeded) {
      seeded = true
      reported.set(page.props.current_user_id)
    }

    return reported.subscribe(run)
  }
}

if (!import.meta.env.SSR) {
  // A response that has been swapped in, with the page taken off the event
  // rather than read back out of the store: what is recorded is what the server
  // actually answered with. Only the page the browser is showing, though - the
  // sidebar's `logged_out` frame is a frame, and a frame's own responses fire
  // `success` as well, carrying the partial prop set the frame asked for and
  // saying nothing about who is signed in.
  //
  // (Document rather than window: these are not dispatched with `bubbles`,
  // which is how `flash.js` hears `inertia:flash` too.)
  document.addEventListener("inertia:success", (event) => {
    const { page: updated, frame } = event.detail
    if (frame !== DEFAULT_FRAME) return

    reported.set(updated.props.current_user_id)
  })
}

export const userId = distinct(served)

/**
 * Like Dexie's `liveQuery`, but the querier receives the current `userId`
 * and the query is re-created whenever `userId` changes.
 *
 * The querier is not called while logged out, which also avoids Dexie
 * throwing on null/undefined keys (`where(...).equals(null)`).
 *
 * @example
 *   export const balances = userQuery(
 *     (id) => db.balances.where("user_id").equals(id).toArray(),
 *     []
 *   )
 *
 * @param {(userId: number) => unknown} querier
 * @param {unknown} [initialValue] emitted while logged out, before the first result
 */
export function userQuery(querier, initialValue = undefined) {
  return derived(userId, ($userId, set) => {
    if ($userId == null) {
      set(initialValue)
      return
    }

    const subscription = liveQuery(() => querier($userId)).subscribe({
      next: set,
      error: (error) => console.error("userQuery failed", error)
    })

    return () => subscription.unsubscribe()
  }, initialValue)
}

export const currentUser = userQuery((id) =>
  db.users.where("id").equals(id).first()
)

export const balances = userQuery((id) =>
  db.balances.where("user_id").equals(id).toArray()
)

export const deposits = userQuery((id) =>
  db.deposits.where("user_id").equals(id).toArray(), []
)

export const orders = userQuery((id) =>
  db.orders.where("user_id").equals(id).toArray(), []
)

/**
 * The signed-in user's own fills, newest first. A trade has two accounts, so
 * these are the ones they were either side of - and both of them get the row
 * over the cable (see Trade.syncs_to_dexie). `sortBy` only ever comes back
 * ascending, hence the reverse.
 */
export const trades = userQuery(async (id) =>
  (await db.trades
    .where("buyer_id").equals(id)
    .or("seller_id").equals(id)
    .sortBy("created_at")).reverse(), []
)

/**
 * Best bid/ask samples for every pair, oldest first. Quotes are broadcast on
 * the public "global" stream (see setup.svelte.js), so this stays reactive to
 * new quotes without the user being signed in.
 */
export const quotes = liveQuery(() =>
  db.quotes.orderBy("created_at").toArray()
)

export const btcUSD = writable(0)

const BTC_USD_ENDPOINT = "https://api.coinbase.com/v2/prices/BTC-USD/spot"

async function updateBTCUSD() {
  const response = await fetch(BTC_USD_ENDPOINT)
  if (!response.ok) throw new Error(`BTC price request failed: ${response.status}`)

  const { data } = await response.json()
  const price = Number(data?.amount)
  if (Number.isFinite(price)) btcUSD.set(price)
}

updateBTCUSD().catch((error) => console.error("updateBTCUSD failed", error))

if (!import.meta.env.SSR) {
  setInterval(() => {
    updateBTCUSD().catch((error) => console.error("updateBTCUSD failed", error))
  }, 3000)
}

